import React from 'react';
import {
  getPlayerPositionAtStep,
  getPassBallPosition,
  getShotBallPosition,
  getPointOnLine,
  getOffsetStartPoint,
  smoothPath,
  getChevronPositions,
  getStepBadgePosition,
  getBallEmoji,
} from '../../utils/plays/animationEngine';
// No import from ./index — avoids circular dep. Caller passes shotTarget in ctx.

const GHOST_RADIUS = 3;

/**
 * @typedef {Object} RenderCtx
 * @property {number} scaleY                Y-axis scale factor (aspect.h / 100)
 * @property {Array}  positions             Player position objects
 * @property {Array}  paths                 Movement path items (cross-referenced by passes/screens)
 * @property {number} currentStep           Current animation step
 * @property {boolean} isPlaying            Whether animation is running
 * @property {number} animationProgress     0-1 within the current step
 * @property {string|null} spotlightPlayer  If set, non-spotlight items are dimmed
 * @property {string} sportId               Sport id (for ball emoji lookup)
 * @property {'half'|'full'} surfaceView    Court view — used by shot target
 */

/**
 * Render one movement/dribble/cut path.
 * @param {Object} path
 * @param {RenderCtx} ctx
 */
export const renderPathItem = (path, ctx) => {
  const { scaleY, positions, currentStep, sportId } = ctx;
  const stepNum = path.stepNumber || 0;

  let pathOpacity = 0.85;
  let showPath = true;
  if (currentStep === 0) showPath = false;
  else if (stepNum < currentStep) pathOpacity = 0.3;
  else if (stepNum === currentStep) pathOpacity = 1.0;
  else showPath = false;
  if (!showPath) return null;

  const player = positions.find((p) => p.id === path.positionId);
  const playerColor = path.color || player?.color || '#3b82f6';
  const pathType = path.type || 'movement';
  const startPoint = path.points[0];

  const offsetStart = getOffsetStartPoint(path.points, GHOST_RADIUS);
  const offsetPoints = [offsetStart, ...path.points.slice(1)];
  const d = smoothPath(offsetPoints);
  const chevronPositions = getChevronPositions(offsetPoints);
  const chevronCount = chevronPositions.length;
  const badge = offsetPoints.length >= 2 ? getStepBadgePosition(offsetPoints) : null;

  return (
    <g key={path.id} opacity={pathOpacity}>
      <circle
        cx={startPoint.x} cy={startPoint.y * scaleY}
        r={GHOST_RADIUS} fill="none" stroke={playerColor}
        strokeWidth="0.5" strokeDasharray="2,1" opacity="0.5"
      />
      <text
        x={startPoint.x} y={startPoint.y * scaleY + 0.3}
        textAnchor="middle" dominantBaseline="middle"
        fill={playerColor} fontSize="2.2" fontWeight="bold" opacity="0.4"
      >{player?.positionLabel || ''}</text>

      <path d={d} fill="none" stroke="#fff" strokeWidth="1.4"
        strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
      <path d={d} fill="none" stroke={playerColor} strokeWidth="0.6"
        strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray={pathType === 'movement' || path.lineStyle === 'dashed' ? '2,1.5' : 'none'}
        opacity="0.85" />

      {pathType === 'dribble' && chevronPositions.map((pt, idx) => (
        <g key={`drib-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
          transform={`translate(${pt.x}, ${pt.y})`}>
          <text y="0.3" textAnchor="middle" dominantBaseline="middle"
            fontSize="3" style={{ filter: 'drop-shadow(0 0 1px #fff)' }}>
            {getBallEmoji(sportId)}
          </text>
        </g>
      ))}

      {pathType === 'cut' && chevronPositions.map((pt, idx) => (
        <g key={`cut-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
          transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
          <path d="M-1.8,-1.2 L-0.6,0 L-1.8,1.2" fill="none" stroke={playerColor}
            strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M-0.6,-1.2 L0.6,0 L-0.6,1.2" fill="none" stroke={playerColor}
            strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
      ))}

      {pathType === 'movement' && chevronPositions.map((pt, idx) => (
        <g key={`chev-${idx}`} className={`chevron-${idx + 1}-of-${chevronCount}`}
          transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.angle})`}>
          <path d="M-1.2,-1 L0,0 L-1.2,1" fill="none" stroke={playerColor}
            strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
      ))}

      {badge && (
        <g>
          <circle cx={badge.x} cy={badge.y} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
          <text x={badge.x} y={badge.y + 0.3} textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
        </g>
      )}
    </g>
  );
};

/**
 * Render one pass (straight / bounce / lob).
 */
export const renderPassItem = (pass, ctx) => {
  const { scaleY, positions, paths, currentStep, isPlaying, animationProgress, sportId } = ctx;
  const stepNum = pass.stepNumber || 1;

  let passOpacity = 0.85;
  let showPass = true;
  let showAnimatedBall = false;
  let ballProgress = 0;

  if (currentStep === 0) showPass = false;
  else if (stepNum < currentStep) passOpacity = 0.3;
  else if (stepNum === currentStep) {
    passOpacity = 1.0;
    if (isPlaying) {
      showAnimatedBall = true;
      ballProgress = animationProgress;
    }
  } else showPass = false;
  if (!showPass) return null;

  const fromPos = getPlayerPositionAtStep(pass.fromPositionId, stepNum - 1, positions, paths) || pass.from;
  const toPos = getPlayerPositionAtStep(pass.toPositionId, stepNum - 1, positions, paths) || pass.to;
  const passer = positions.find((p) => p.id === pass.fromPositionId);
  const passerColor = passer?.color || '#374151';

  const fromScaled = { x: fromPos.x, y: fromPos.y * scaleY };
  const toScaled = { x: toPos.x, y: toPos.y * scaleY };

  const d = pass.type === 'lob'
    ? `M ${fromScaled.x},${fromScaled.y} Q ${(fromScaled.x + toScaled.x) / 2},${Math.min(fromScaled.y, toScaled.y) - 8} ${toScaled.x},${toScaled.y}`
    : `M ${fromScaled.x},${fromScaled.y} L ${toScaled.x},${toScaled.y}`;

  return (
    <g key={pass.id} opacity={passOpacity}>
      <path d={d} fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
      <path d={d} fill="none" stroke={passerColor} strokeWidth="0.6"
        strokeDasharray={pass.type === 'bounce' ? '2,2' : 'none'}
        strokeLinecap="round" />

      {(() => {
        const dx = toScaled.x - fromScaled.x;
        const dy = toScaled.y - fromScaled.y;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        return (
          <g transform={`translate(${toScaled.x}, ${toScaled.y}) rotate(${angle})`}>
            <path d="M-3,-1.5 L0,0 L-3,1.5" fill="none" stroke={passerColor}
              strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round"/>
          </g>
        );
      })()}

      {showAnimatedBall && (() => {
        const ballPos = getPassBallPosition(fromScaled, toScaled, ballProgress, pass.type);
        return (
          <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
            <circle r="2.5" fill="#f97316" stroke="#fff" strokeWidth="0.3" />
            <text y="0.4" textAnchor="middle" dominantBaseline="middle" fontSize="3.5"
              style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}>
              {getBallEmoji(sportId)}
            </text>
          </g>
        );
      })()}

      {!isPlaying && (() => {
        const midPoint = getPointOnLine(fromScaled, toScaled, 0.5);
        return (
          <g transform={`translate(${midPoint.x}, ${midPoint.y})`}>
            <text y="0.3" textAnchor="middle" dominantBaseline="middle"
              fontSize="2.5" opacity="0.6">{getBallEmoji(sportId)}</text>
          </g>
        );
      })()}

      {(() => {
        const dx = toScaled.x - fromScaled.x;
        const dy = toScaled.y - fromScaled.y;
        const midX = fromScaled.x + dx * 0.4;
        const midY = fromScaled.y + dy * 0.4;
        const angle = Math.atan2(dy, dx);
        const perpAngle = angle + Math.PI / 2;
        const badgeX = midX + Math.cos(perpAngle) * 3;
        const badgeY = midY + Math.sin(perpAngle) * 3;
        return (
          <g>
            <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
            <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle"
              fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
          </g>
        );
      })()}
    </g>
  );
};

/**
 * Render one screen (pick).
 */
export const renderScreenItem = (screen, ctx, i) => {
  const { scaleY, positions, paths, currentStep, isPlaying, animationProgress } = ctx;
  const setter = positions.find((p) => p.id === screen.setterId);
  const setterColor = setter?.color || '#374151';
  const screenX = screen.x;
  const screenY = screen.y * scaleY;
  const rotation = screen.rotation || 0;
  const stepNum = screen.stepNumber || 1;

  const setterRollPath = paths.find((p) =>
    p.positionId === screen.setterId &&
    (p.stepNumber || 1) > stepNum &&
    !p.isScreenMove
  );
  const rollStep = setterRollPath?.stepNumber || Infinity;

  const screenMovePath = paths.find((p) =>
    p.positionId === screen.setterId &&
    p.isScreenMove &&
    (p.stepNumber || 1) === stepNum
  );

  let screenOpacity = 1;
  let showScreen = true;

  if (currentStep === 0) showScreen = false;
  else if (currentStep < stepNum) showScreen = false;
  else if (currentStep >= rollStep) showScreen = false;
  else if (isPlaying && currentStep === stepNum && screenMovePath) {
    if (animationProgress < 0.95) showScreen = false;
    else screenOpacity = 0.5 + animationProgress * 0.5;
  }
  if (!showScreen) return null;

  const widthMult = 1.2;
  const heightMult = 0.6;
  const barW = 10 * widthMult;
  const barH = 4 * heightMult;

  return (
    <g key={screen.id || `screen-${i}`} opacity={screenOpacity}>
      <g transform={`translate(${screenX}, ${screenY}) rotate(${rotation})`}>
        <rect
          x={-barW / 2} y={-barH / 2}
          width={barW} height={barH}
          rx={(screen.shape || 'sharp') === 'rounded' ? 1.5 : 0}
          fill={setterColor} stroke="#fff" strokeWidth="0.3"
        />
      </g>
      <circle cx={screenX} cy={screenY} r="2"
        fill={setterColor} stroke="#fff" strokeWidth="0.4" />
      <text x={screenX} y={screenY + 0.3}
        textAnchor="middle" dominantBaseline="middle"
        fill="#fff" fontSize="1.8" fontWeight="bold"
        style={{ pointerEvents: 'none' }}>
        {setter?.positionLabel || '?'}
      </text>
    </g>
  );
};

/**
 * Render one shot with view-aware target (fixes the v0.7.0 regression).
 */
export const renderShotItem = (shot, ctx, idx) => {
  const { scaleY, positions, paths, currentStep, isPlaying, animationProgress, spotlightPlayer, sportId, shotTarget } = ctx;
  const stepNum = shot.stepNumber || 1;
  if (!shotTarget) return null;

  let shotOpacity = 0.9;
  let showShot = true;
  let showAnimatedBall = false;
  let ballProgress = 0;

  if (currentStep === 0) showShot = false;
  else if (stepNum < currentStep) shotOpacity = 0.3;
  else if (stepNum === currentStep) {
    shotOpacity = 1.0;
    if (isPlaying) {
      showAnimatedBall = true;
      ballProgress = animationProgress;
    }
  } else showShot = false;

  if (spotlightPlayer && shot.shooterId !== spotlightPlayer) {
    shotOpacity *= 0.25;
  }
  if (!showShot) return null;

  const shooterPos = getPlayerPositionAtStep(shot.shooterId, stepNum - 1, positions, paths);
  if (!shooterPos) return null;

  const shooterScaled = { x: shooterPos.x, y: shooterPos.y * scaleY };
  const targetScaled = { x: shotTarget.x, y: shotTarget.y * scaleY };

  return (
    <g key={shot.id || `shot-${idx}`} opacity={shotOpacity}>
      {showAnimatedBall && (() => {
        const ballPos = getShotBallPosition(shooterScaled, targetScaled, ballProgress);
        return (
          <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
            <circle r={2.2 * ballPos.scale} fill="#ea580c" stroke="#fff" strokeWidth="0.3" />
            <text y="0.3" textAnchor="middle" dominantBaseline="middle"
              fontSize={3.2 * ballPos.scale}
              style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }}>
              {getBallEmoji(sportId)}
            </text>
          </g>
        );
      })()}

      {!isPlaying && (
        <g transform={`translate(${targetScaled.x}, ${targetScaled.y - 2})`}>
          <circle r="1.8" fill="#ea580c" stroke="#fff" strokeWidth="0.2" opacity="0.8" />
          <text y="0.2" textAnchor="middle" dominantBaseline="middle"
            fontSize="2.5" opacity="0.9">{getBallEmoji(sportId)}</text>
        </g>
      )}

      {showAnimatedBall && ballProgress > 0.85 && (
        <g transform={`translate(${targetScaled.x}, ${targetScaled.y})`}>
          <circle r={3 + (ballProgress - 0.85) * 20} fill="none"
            stroke="#f59e0b" strokeWidth="0.5"
            opacity={1 - (ballProgress - 0.85) * 6} />
        </g>
      )}

      {(() => {
        const badgePos = getShotBallPosition(shooterScaled, targetScaled, 0.15);
        const dx = targetScaled.x - shooterScaled.x;
        const dy = targetScaled.y - shooterScaled.y;
        const angle = Math.atan2(dy, dx);
        const perpAngle = angle + Math.PI / 2;
        const badgeX = badgePos.x + Math.cos(perpAngle) * 3;
        const badgeY = badgePos.y + Math.sin(perpAngle) * 3;
        return (
          <g>
            <circle cx={badgeX} cy={badgeY} r="2" fill="#1e3a5f" stroke="#fff" strokeWidth="0.3" />
            <text x={badgeX} y={badgeY + 0.3} textAnchor="middle" dominantBaseline="middle"
              fill="#fff" fontSize="2" fontWeight="bold">{stepNum}</text>
          </g>
        );
      })()}
    </g>
  );
};
